import { MARKETS, marketToCountry, type Market } from "@/i18n/markets";

export interface CountryPickerLanguage {
  code: string;
  label: string;
}

export interface CountryPickerCountry {
  market: Market;
  countryCode: string;
  countryName: string;
  languages: CountryPickerLanguage[];
}

export const LANGUAGE_LABELS: Readonly<Record<string, string>> = {
  en: "English",
  fr: "Français",
  de: "Deutsch",
  es: "Español",
  it: "Italiano",
  nl: "Nederlands",
  da: "Dansk",
  sv: "Svenska",
  no: "Norsk",
  pt: "Português",
};

function localizedCountryName(countryCode: string, locale: string, fallback: string): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: "region" }).of(countryCode);
    return name && name !== countryCode ? name : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Build the country-first language picker from the same market/language
 * matrix used by locale URL parsing. A market is omitted if none of its
 * supported languages are currently enabled; no unsupported language is
 * invented as a fallback.
 */
export function getCountryPickerCountries(
  locale: string,
  enabledLanguageCodes: readonly string[],
): CountryPickerCountry[] {
  const enabledLanguages = new Set(enabledLanguageCodes);

  return MARKETS.flatMap((market) => {
    if (market.code === "global") return [];
    const countryCode = marketToCountry(market.code);
    if (!countryCode) return [];

    const languages = market.languages
      .filter((code) => code === "en" || enabledLanguages.has(code))
      .map((code) => ({
        code,
        label: LANGUAGE_LABELS[code] || code.toUpperCase(),
      }));
    if (languages.length === 0) return [];

    return [{
      market,
      countryCode,
      countryName: localizedCountryName(countryCode, locale, market.nativeLabel || market.label),
      languages,
    }];
  });
}

function normalizeSearchText(value: string, locale: string): string {
  return value
    .toLocaleLowerCase(locale)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim();
}

/** Match country names and verified market/country codes, not language names. */
export function filterCountryPickerCountries(
  countries: readonly CountryPickerCountry[],
  query: string,
  locale: string,
): CountryPickerCountry[] {
  const normalizedQuery = normalizeSearchText(query, locale);
  if (!normalizedQuery) return [...countries];

  // Two-letter queries commonly mean an ISO-2 country code. Prefer the
  // exact country/market-code match so e.g. "CA" doesn't also match
  // "South Africa" just because those letters appear inside its name.
  if (/^[a-z]{2}$/i.test(query.trim())) {
    const codeMatches = countries.filter(({ market, countryCode }) =>
      [countryCode, market.code].some((code) => normalizeSearchText(code, locale) === normalizedQuery),
    );
    if (codeMatches.length > 0) return codeMatches;
  }

  return countries.filter(({ market, countryCode, countryName }) =>
    [countryName, market.nativeLabel, market.label, countryCode, market.code]
      .some((term) => normalizeSearchText(term, locale).includes(normalizedQuery)),
  );
}

export interface CountryPickerGroup {
  letter: string;
  countries: CountryPickerCountry[];
}

/** Sort country choices and their section letters for CSS multi-column flow. */
export function groupCountryPickerCountries(
  countries: readonly CountryPickerCountry[],
  locale: string,
): CountryPickerGroup[] {
  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  const sorted = [...countries].sort((a, b) =>
    collator.compare(a.countryName, b.countryName),
  );
  const groups = new Map<string, CountryPickerCountry[]>();

  for (const country of sorted) {
    const firstCharacter = Array.from(country.countryName)[0] || "#";
    const letter = firstCharacter.toLocaleUpperCase(locale);
    const group = groups.get(letter) || [];
    group.push(country);
    groups.set(letter, group);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => collator.compare(a, b))
    .map(([letter, groupedCountries]) => ({ letter, countries: groupedCountries }));
}